import React from 'react';

import {
  BorderColor as BorderColorIcon,
  EditNote as EditAllIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';

import { convertCentsToDollars, formatMoney } from '@clearverify/utils';

import { VerifierBillingListQuery } from 'src/api';
import { Tooltip } from 'src/components/common';
import type { TableColumn, TableSelectedRowId } from 'src/components/table';
import {
  Table,
  TableExcelColumn,
  TableFilter,
  TableRowAction,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { FilterKeys } from 'src/constants/filter';
import { ModalId } from 'src/constants/modals';
import { useVerifierBillingList } from 'src/hooks/billings/useVerifierBillingList';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { useVerifierAutocomplete } from 'src/hooks/verifier';

export const VerifiersBillingTable: React.FC = () => {
  const { verifiersBillings, verifiersBillingsListCount, loading } = useVerifierBillingList();
  const verifiersSelectProps = useVerifierAutocomplete();
  const { openModal } = useModal();

  const { setSearchValue, searchValue } = useSearch();
  const { getPaginationObjectByKey } = useFilterStorage();
  const pagination = getPaginationObjectByKey(FilterKeys.VerifiersBillingTable);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  const topActions: Array<TableTopAction> = React.useMemo(
    () => [
      {
        actionName: 'Edit selected verifiers',
        show: selectedIds.length > 0,
        icon: <EditIcon />,
        onAction: () =>
          openModal(ModalId.VerifierBillingRulesChangeModal, {
            billingRuleIds: selectedIds,
          }),
        render() {
          return (
            <Tooltip title={this.actionName}>
              <IconButton key={this.actionName} onClick={this.onAction} disabled={loading}>
                {this.icon}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        actionName: 'Edit all verifiers',
        icon: <EditAllIcon />,
        onAction: () =>
          openModal(ModalId.VerifierBillingRulesChangeModal, {
            billingRuleIds: [],
            isAllSelected: true,
          }),
        render() {
          return (
            <Tooltip title={this.actionName}>
              <IconButton key={this.actionName} onClick={this.onAction} disabled={loading}>
                {this.icon}
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [loading, openModal, selectedIds],
  );

  const rowActions: Array<TableRowAction<VerifierBillingListQuery['verifierBillingList'][0]>> =
    React.useMemo(
      () => [
        {
          startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
          actionName: 'View Details',
          onAction: verifierBilling =>
            openModal(ModalId.VerifierBillingRulesChangeModal, {
              billingRuleIds: verifierBilling.id,
              voi: convertCentsToDollars(verifierBilling.voI),
              voe: convertCentsToDollars(verifierBilling.voE),
              reverification: convertCentsToDollars(verifierBilling.reverification),
            }),
        },
      ],
      [openModal],
    );

  const filters = React.useMemo(
    (): TableFilter[] => [
      {
        id: 'verifier',
        type: 'autocomplete',
        fieldProps: {
          label: 'Verifier Name',
          ...verifiersSelectProps,
        },
      },
    ],
    [verifiersSelectProps],
  );

  return (
    <Table
      dataSource={verifiersBillings}
      rowCount={verifiersBillingsListCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      pagination={pagination}
      loading={loading}
      selectable
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      excelColumns={excelColumns}
      filters={filters}
      tableTheme={TableThemes.withBorder}
    />
  );
};

const columns: Array<TableColumn<VerifierBillingListQuery['verifierBillingList'][0]>> = [
  {
    title: 'Name',
    key: 'verifier.displayName',
    render: (_valueByDataPath, verifiersBilling) => {
      return <Typography variant="body2">{verifiersBilling.verifier.displayName}</Typography>;
    },
  },
  {
    title: 'VoI',
    key: 'voi',
    render: (_valueByDataPath, verifiersBilling) => {
      return <Typography variant="body2">{formatMoney(verifiersBilling.voI, 0)}</Typography>;
    },
  },
  {
    title: 'VoE',
    key: 'voe',
    render: (_valueByDataPath, verifiersBilling) => {
      return <Typography variant="body2">{formatMoney(verifiersBilling.voE, 0)}</Typography>;
    },
  },
  {
    title: 'Reverification',
    key: 'reverification',
    render: (_valueByDataPath, verifiersBilling) => {
      return (
        <Typography variant="body2">{formatMoney(verifiersBilling.reverification, 0)}</Typography>
      );
    },
  },
];

const excelColumns: Array<TableExcelColumn<VerifierBillingListQuery['verifierBillingList'][0]>> = [
  {
    header: 'Name',
    key: 'verifier.displayName',
  },
  {
    header: 'VoI',
    key: 'voI',
    render: (_, verifierBilling) => {
      return convertCentsToDollars(verifierBilling.voI);
    },
  },
  {
    header: 'VoE',
    key: 'voE',
    render: (_, verifierBilling) => {
      return convertCentsToDollars(verifierBilling.voE);
    },
  },
  {
    header: 'Reverification',
    key: 'reverification',
    render: (_, verifierBilling) => {
      return convertCentsToDollars(verifierBilling.reverification);
    },
  },
];
